// aifab - RunRow component
import { useState, useEffect } from 'react';
import { apiFetch } from '../../hooks/apiFetch';
import { Spinner, StatusBadge, Modal, ModalBody, ModalFooter, Err, Hint } from '../common';
import { useApi } from '../../hooks/useApi';
import { PLUGIN_COLORS } from '../../constants/plugins';
import { PredictModal } from './PredictModal';
import { LogViewer } from './LogViewer';
import { CloneRunModal } from './CloneRunModal';

const PURPOSE_HINTS = {
  DOMAIN_PRETRAIN: "Adapts a base model's language understanding to your text, without predicting any specific label. Produces a checkpoint other runs can build on.",
  REGRESSION:      'Predicts a continuous number (e.g. a price or duration) rather than a category.',
  NER:             'Named Entity Recognition - tags specific spans of text (names, dates, IDs) rather than classifying the whole input.',
  CUSTOM:          'Runs a user-provided script instead of one of the built-in training templates.',
};

// ── ElapsedTimer ──────────────────────────────────────────────────────────────
function ElapsedTimer({ startedAt }) {
  const [elapsed, setElapsed] = useState(() =>
    startedAt ? Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000) : 0
  );
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() =>
      setElapsed(Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000))
    , 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  const h   = Math.floor(elapsed / 3600);
  const m   = Math.floor((elapsed % 3600) / 60);
  const s   = elapsed % 60;
  const fmt = h > 0
    ? `${h}h ${String(m).padStart(2,'0')}m`
    : `${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`;

  return (
    <span style={{
      fontFamily:'var(--f-mono)', fontSize:11, color:'var(--or)',
      background:'rgba(232,71,10,.08)', padding:'2px 7px',
      borderRadius:3, border:'1px solid rgba(232,71,10,.2)',
      display:'inline-flex', alignItems:'center', gap:4,
    }}>
      <span style={{fontSize:8,opacity:.6}}>⏱</span>{fmt}
    </span>
  );
}

// ── RunDuration ───────────────────────────────────────────────────────────────
// Shows elapsed time for running jobs, total duration for terminal jobs.
function RunDuration({ run }) {
  const ACTIVE   = new Set(['RUNNING', 'QUEUED', 'WAITING_FOR_RESOURCES']);
  const TERMINAL = new Set(['COMPLETED', 'FAILED', 'FAILED_DATA', 'TIMEOUT', 'CANCELLED']);

  if (ACTIVE.has(run.status) && run.started_at) {
    return <ElapsedTimer startedAt={run.started_at} />;
  }

  if (TERMINAL.has(run.status) && run.started_at) {
    const end   = run.completed_at ? new Date(run.completed_at) : new Date(run.updated_at);
    const secs  = Math.max(0, Math.floor((end - new Date(run.started_at)) / 1000));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    const fmt = h > 0
      ? `${h}h ${String(m).padStart(2,'0')}m`
      : m > 0
        ? `${m}m ${String(s).padStart(2,'0')}s`
        : `${s}s`;
    const color = run.status === 'COMPLETED' ? 'var(--green)'
                : run.status === 'CANCELLED' ? '#64748B'
                : 'var(--red)';
    return (
      <span style={{
        fontFamily:'var(--f-mono)', fontSize:11, color,
        background:'#F1F5FB', padding:'2px 7px', border:'1px solid #C8D5E8',
        borderRadius:3, border:'1px solid var(--bd)',
        display:'inline-flex', alignItems:'center', gap:4,
      }}>
        <span style={{fontSize:8,opacity:.5}}>⏱</span>{fmt}
      </span>
    );
  }

  return null;
}

export function RunRow({ run, onReload, onDeleted, onCloned, selected, onSelect }) {
  const [expanded,    setExpanded]    = useState(false);
  const [detail,      setDetail]      = useState(null);
  const [activeJobId, setActiveJobId] = useState(null); // set immediately on start, before detail re-fetch

  const [showStart,   setShowStart]   = useState(false);
  const [showPredict, setShowPredict] = useState(false);
  const [showClone,   setShowClone]   = useState(false);
  const [showDelete,  setShowDelete]  = useState(false);
  const [packaging,   setPackaging]   = useState(false);
  // pkgInfo - persisted package data (survives page refresh)
  // showPkg  - controls modal visibility (never auto-shown on mount)
  const [pkgInfo,    setPkgInfo]    = useState(() => {
    try {
      const stored = localStorage.getItem(`aifab_pkg_${run.id}`);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [showPkg,    setShowPkg]    = useState(false);

  const toggleExpand = async () => {
    if (!expanded && !detail) {
      try {
        const d = await apiFetch(`/runs/${run.id}/detail`);
        setDetail(d);
      } catch (_) {}
    }
    setExpanded(!expanded);
  };

  const openClone = async () => {
    // Load detail first so CloneRunModal has params_snapshot
    if (!detail) {
      try { const d = await apiFetch(`/runs/${run.id}/detail`); setDetail(d); } catch (_) {}
    }
    setShowClone(true);
  };

  const cancelRun = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Cancel Run #${run.run_number}? This will kill the active job.`)) return;
    try {
      await apiFetch(`/admin/runs/${run.id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Cancelled by user' }),
      });
      onReload();
    } catch (e) { alert(`Cancel failed: ${e.message}`); }
  };

  const isComparable   = run.status === 'COMPLETED';
  const isRunning      = ['RUNNING', 'QUEUED', 'WAITING_FOR_RESOURCES'].includes(run.status);
  const canPredict     = run.status === 'COMPLETED';

  const openPredict = async () => {
    if (!detail) {
      try { const d = await apiFetch(`/runs/${run.id}/detail`); setDetail(d); } catch (_) {}
    }
    setShowPredict(true);
  };

  const packageModel = async () => {
    setPackaging(true);
    try {
      const result = await apiFetch(`/runs/${run.id}/package`, { method: 'POST' });
      setPkgInfo(result);
      setShowPkg(true);
      try { localStorage.setItem(`aifab_pkg_${run.id}`, JSON.stringify(result)); } catch {}
    } catch(e) {
      setPkgInfo({ error: e.message });
      setShowPkg(true);
    } finally {
      setPackaging(false);
    }
  };

  return (
    <div>
      {/* ── Row header ───────────────────────────────────────── */}
      <div className={`run-row${selected ? ' selected' : ''}`} style={{ cursor: 'pointer' }}>
        <div className="run-num" onClick={toggleExpand}>#{run.run_number}</div>

        <div className="run-meta" onClick={toggleExpand} style={{ flex: 1, minWidth: 0 }}>
          <div className="run-label" style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <span>{run.run_label || `Run #${run.run_number}`}</span>
            {run.run_purpose && run.run_purpose !== 'CLASSIFICATION' && (
              <Hint text={PURPOSE_HINTS[run.run_purpose] || 'A non-standard run purpose.'}>
                <span style={{
                  fontSize:10, fontWeight:600, padding:'1px 7px', borderRadius:4,
                  background: {
                    DOMAIN_PRETRAIN: 'rgba(22,163,74,0.1)',
                    REGRESSION:      'rgba(37,99,235,0.1)',
                    NER:             'rgba(124,58,237,0.1)',
                    CUSTOM:          'rgba(107,114,148,0.1)',
                  }[run.run_purpose] || 'rgba(107,114,148,0.1)',
                  color: {
                    DOMAIN_PRETRAIN: 'var(--success)',
                    REGRESSION:      'var(--info)',
                    NER:             '#7c3aed',
                    CUSTOM:          'var(--text-secondary)',
                  }[run.run_purpose] || 'var(--text-secondary)',
                }}>
                  {{
                    DOMAIN_PRETRAIN: '🧠 Domain Pre-train',
                    REGRESSION:      '📈 Regression',
                    NER:             '🏷️ NER',
                    CUSTOM:          '⚙️ Custom',
                  }[run.run_purpose] || run.run_purpose}
                </span>
              </Hint>
            )}
            {run.model_display_name && (
              <span className="badge b-or" style={{ fontWeight:500 }}>{run.model_display_name}</span>
            )}
            {run.model_family_code && (
              <span className="badge b-gray" style={{ fontSize:10 }}>{run.model_family_code}</span>
            )}
          </div>
          <div className="run-sub flex-row" style={{ flexWrap:'wrap', gap:6, marginTop:4 }}>
            <StatusBadge code={run.status} />
            <RunDuration run={run} />
            {run.priority !== 'NORMAL' && (
              <span className={`badge ${run.priority === 'HIGH' ? 'b-orange' : 'b-slate'}`}>{run.priority}</span>
            )}
            {run.datasource_name && (
              <span className="sm c-sl">
                🗄 Dataset: {run.datasource_name}
                {run.datasource_version_number != null &&
                  <span style={{color:'var(--or)',marginLeft:3}}>v{run.datasource_version_number}</span>}
              </span>
            )}
            {run.target_column && (
              <span className="sm c-sl">Target: <span className="mono" style={{ color:'var(--or)' }}>{run.target_column}</span></span>
            )}
            {run.feature_config_name && (
              <span className="sm c-sl">⚙ Features: {run.feature_config_name}</span>
            )}
            {run.pipeline_template_id && (
              <Hint text="Pre/post-processing plugins run alongside training or inference for this run (e.g. data cleanup, custom postprocessing).">
                <span className="sm c-sl">⬡ Pipeline attached</span>
              </Hint>
            )}
            {run.wait_reason && (
              <Hint text="The run is queued but can't start yet - usually because the server is busy, base model weights are still downloading, or resources are unavailable. It will proceed automatically once the blocker clears.">
                <span className="sm c-ylw">{run.wait_reason}</span>
              </Hint>
            )}
            {run.parent_run_id && (
              <Hint text="This run was created by cloning an earlier run's configuration (model, dataset, feature config, pipeline) as a starting point.">
                <span className="sm c-sl">cloned</span>
              </Hint>
            )}
          </div>
          {run.status === 'WAITING_FOR_RESOURCES' && run.error_message && (
            <div style={{ fontSize: 12, color: '#B45309', marginTop: 4,
              maxWidth: 720, lineHeight: 1.5, wordBreak: 'break-word' }}>
              {run.error_message}
            </div>
          )}
          {['FAILED', 'FAILED_DATA', 'TIMEOUT'].includes(run.status) && run.error_message && (
            <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4,
              maxWidth: 720, lineHeight: 1.5, wordBreak: 'break-word' }}>
              {run.error_class && <strong style={{ marginRight: 6 }}>{run.error_class}{run.failed_stage ? ` · ${run.failed_stage}` : ''}</strong>}
              {run.error_message}
            </div>
          )}
        </div>

        {/* Purpose-aware metrics display */}
        {run.run_purpose === 'REGRESSION' && run.rmse != null && (
          <div className="run-metrics">
            <div className="run-metric">
              <div className="run-metric-val">{run.rmse.toFixed(4)}</div>
              <div className="run-metric-lbl">RMSE</div>
            </div>
            {run.mae != null && (
              <div className="run-metric">
                <div className="run-metric-val">{run.mae.toFixed(4)}</div>
                <div className="run-metric-lbl">MAE</div>
              </div>
            )}
            {run.r2_score != null && (
              <div className="run-metric">
                <div className="run-metric-val">{run.r2_score.toFixed(3)}</div>
                <div className="run-metric-lbl">R²</div>
              </div>
            )}
          </div>
        )}
        {run.run_purpose === 'DOMAIN_PRETRAIN' && run.mlm_loss != null && (
          <div className="run-metrics">
            <div className="run-metric">
              <div className="run-metric-val">{run.mlm_loss.toFixed(4)}</div>
              <div className="run-metric-lbl">MLM Loss</div>
            </div>
            {run.perplexity != null && (
              <div className="run-metric">
                <div className="run-metric-val">{run.perplexity.toFixed(2)}</div>
                <div className="run-metric-lbl">Perplexity</div>
              </div>
            )}
          </div>
        )}
        {(run.run_purpose === 'CLASSIFICATION' || run.run_purpose === 'NER' || !run.run_purpose) && run.f1_score != null && (
          <div className="run-metrics">
            <div className="run-metric">
              <div className="run-metric-val">{(run.f1_score * 100).toFixed(1)}%</div>
              <div className="run-metric-lbl">F1</div>
            </div>
            {run.accuracy != null && (
              <div className="run-metric">
                <div className="run-metric-val">{(run.accuracy * 100).toFixed(1)}%</div>
                <div className="run-metric-lbl">Acc</div>
              </div>
            )}
          </div>
        )}

        <div className="flex-row" style={{ flexShrink: 0 }}>
          {isComparable && (
            <button className="btn btn-ghost btn-sm" title="Select for side-by-side comparison"
              onClick={e => { e.stopPropagation(); onSelect(); }}>⇄</button>
          )}
          {canPredict && (
            <button className="btn btn-ghost btn-sm c-or" style={{ borderColor:'var(--or)' }}
              onClick={e => { e.stopPropagation(); openPredict(); }}
              title="Test this model with a single prediction">
              ▶ Test
            </button>
          )}
          {canPredict && (
            <button className="btn btn-ghost btn-sm"
              style={{ borderColor:'#C8D5E8', fontSize:11 }}
              onClick={e => { e.stopPropagation(); packageModel(); }}
              title="Package model for standalone deployment"
              disabled={packaging}>
              {packaging ? <Spinner size={10}/> : pkgInfo && !pkgInfo.error ? '✓ Packaged' : '📦 Package'}
            </button>
          )}
          {run.status === 'CREATED' && (
            <button className="btn btn-success btn-sm" onClick={e => { e.stopPropagation(); setShowStart(true); }}>▶ Start</button>
          )}
          {isRunning && (
            <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); cancelRun(); }}>✕ Cancel</button>
          )}
          <button className="btn btn-ghost btn-sm" title="Clone this run with optional param changes"
            onClick={e => { e.stopPropagation(); openClone(); }}>Clone</button>
          <button className="btn btn-ghost btn-sm" title="Delete this run"
            style={{ color: 'var(--red)', opacity: 0.7 }}
            onClick={e => { e.stopPropagation(); setShowDelete(true); }}>✕</button>
          <button className="btn btn-ghost btn-sm" onClick={toggleExpand}>{expanded ? '▲' : '▼'}</button>
        </div>
      </div>

      {/* ── Expanded detail panel ─────────────────────────────── */}
      {expanded && detail && (() => {
        const dref = detail.datasource_ref || {};
        const mref = detail.model_config_ref || {};
        const fmtId = (id) => id ? <span className="mono c-sl" style={{fontSize:9}}>{id.slice(0,8)}…</span> : <span className="c-sl">—</span>;
        return (
        <div style={{ background: '#FFFFFF', border: '1px solid #C8D5E8', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px 20px', marginBottom: 8, boxShadow: '0 2px 8px rgba(15,23,42,.06)' }}>

          {/* ── Dataset ─────────────────────────────────────────── */}
          <div style={{ marginBottom: 14 }}>
            <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 10, color:'#334155', fontWeight:700 }}>Dataset</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 24px' }}>
              <span className="sm"><span className="c-sl">Name: </span>{dref.display_name || '—'}</span>
              {dref.version_number != null &&
                <span className="sm"><span className="c-sl">Version: </span><span style={{color:'var(--or)'}}>v{dref.version_number}</span></span>}
              {dref.total_rows != null &&
                <span className="sm"><span className="c-sl">Rows: </span>{dref.total_rows.toLocaleString()}</span>}
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 24px', marginTop:6 }}>
              <span className="xs c-sl">datasource_id: {fmtId(detail.datasource_id || dref.id)}</span>
              <span className="xs c-sl">version_id: {fmtId(detail.datasource_version_id || dref.version_id)}</span>
              {dref.content_hash &&
                <span className="xs c-sl">hash: <span className="mono" style={{fontSize:9}}>{dref.content_hash.slice(0,16)}…</span></span>}
            </div>
            {dref.file_path &&
              <div className="xs mono c-sl" style={{marginTop:4,wordBreak:'break-all'}}>{dref.file_path}</div>}
          </div>

          {/* ── Model ───────────────────────────────────────────── */}
          <div style={{ marginBottom: 14, paddingTop: 12, borderTop: '1px solid var(--bd)' }}>
            <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 10, color:'#334155', fontWeight:700 }}>Model</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 24px' }}>
              <span className="sm"><span className="c-sl">Family: </span>{mref.family_name || '—'}</span>
              <span className="sm"><span className="c-sl">Base: </span>{mref.base_model_name || '—'}</span>
              {mref.version_number != null &&
                <span className="sm"><span className="c-sl">Version: </span><span style={{color:'var(--or)'}}>v{mref.version_number}</span></span>}
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 24px', marginTop:6 }}>
              <span className="xs c-sl">version_id: {fmtId(detail.model_version_id || mref.version_id)}</span>
              {mref.config_id &&
                <span className="xs c-sl">config_id: {fmtId(mref.config_id)}</span>}
            </div>
          </div>

          {/* ── Artifact ────────────────────────────────────────── */}
          {detail.artifact_path && (
            <div style={{ marginBottom: 14, paddingTop: 12, borderTop: '1px solid var(--bd)' }}>
              <div className="flex-btw mb8">
                <div className="sm c-sl" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 10, color:'#334155', fontWeight:700 }}>Artifact</div>
                {canPredict && (
                  <button className="btn btn-ghost btn-sm c-or" style={{ borderColor: 'var(--or)' }}
                    onClick={e => { e.stopPropagation(); openPredict(); }}>
                    ▶ Test Model
                  </button>
                )}
              </div>
              <div className="sm mono c-grn">{detail.artifact_server_name}: {detail.artifact_path}</div>
            </div>
          )}

          {/* ── Execution attempts ──────────────────────────────── */}
          {detail.attempts?.length > 0 && (
            <div style={{ paddingTop: 12, borderTop: '1px solid var(--bd)', marginBottom: 14 }}>
              <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 10, color:'#334155', fontWeight:700 }}>Execution Attempts</div>
              {detail.attempts.map(j => (
                <div key={j.job_id} style={{ marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid var(--bdr)' }}>
                  <div className="flex-row sm" style={{ marginBottom: 3 }}>
                    <span className="c-sl">Attempt {j.attempt_number}</span>
                    <StatusBadge code={j.status} />
                    {j.server_name && <span className="sm c-sl">🖥 {j.server_name}</span>}
                    {j.started_at  && <span className="c-sl">{j.started_at?.slice(0, 16)}</span>}
                    {j.duration_secs != null && <span className="xs c-sl">{j.duration_secs}s</span>}
                    {j.status === 'RUNNING' && j.started_at && <ElapsedTimer startedAt={j.started_at} />}
                    {j.retry_count > 0 && <span className="badge b-slate">{j.retry_count} retries</span>}
                  </div>
                  <div style={{ display:'flex', gap:'4px 24px', flexWrap:'wrap', marginBottom: j.error_class ? 4 : 0 }}>
                    <span className="xs c-sl">job_id: {fmtId(j.job_id)}</span>
                    <span className="xs c-sl">server_id: {fmtId(j.server_id)}</span>
                    <span className="xs c-sl">datasource_id: {fmtId(j.datasource_id)}</span>
                    <span className="xs c-sl">version_id: {fmtId(j.version_id)}</span>
                  </div>
                  {(j.error_class || j.error_message) && (
                    <div style={{ marginTop: 4, padding: '6px 10px', background: 'rgba(239,68,68,.05)', borderRadius: 3, borderLeft: '2px solid var(--red)' }}>
                      <div className="flex-row sm" style={{ marginBottom: 2 }}>
                        {j.error_class   && <span style={{ color: 'var(--red)', fontWeight: 600 }}>{j.error_class}</span>}
                        {j.error_subtype && j.error_subtype !== j.error_class &&
                          <span className="c-sl">· {j.error_subtype}</span>}
                        {j.failed_stage  && <span className="c-sl">· stage: <span style={{ color: '#D97706' }}>{j.failed_stage}</span></span>}
                      </div>
                      {j.error_message && (
                        <div className="mono" style={{ color: '#64748B', fontSize: 10, whiteSpace: 'pre-wrap', maxHeight: 72, overflow: 'hidden' }}>
                          {j.error_message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Lineage ─────────────────────────────────────────── */}
          {detail.parent_run_id && (
            <div style={{ paddingTop: 12, borderTop: '1px solid var(--bd)', marginBottom: 14 }}>
              <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 10, color:'#334155', fontWeight:700 }}>Lineage</div>
              <div className="sm">
                <span className="c-sl">Cloned from</span>
                <span className="c-or" style={{ marginLeft: 8 }}>Run #{detail.clone_source_number || '?'}</span>
                <span className="mono c-sl" style={{ marginLeft: 8, fontSize: 10 }}>{detail.parent_run_id?.slice(0, 8)}…</span>
              </div>
              <div className="sm" style={{ marginTop: 6, display:'flex', gap:16, flexWrap:'wrap' }}>
                <span className="c-sl">Model: <span style={{color:'#0F172A'}}>
                  {mref.display_name} v{mref.version_number}
                </span></span>
                <span className="c-sl">Dataset: <span style={{color:'#0F172A'}}>
                  {dref.display_name}
                  {dref.version_number != null && <span style={{color:'var(--or)',marginLeft:3}}>v{dref.version_number}</span>}
                </span></span>
                <span className="c-sl">Feature config: <span style={{color:'#0F172A'}}>
                  {detail.feature_config_snapshot?._config_name || <span style={{color:'#64748B'}}>—</span>}
                </span></span>
              </div>
              {detail.clone_changes && (
                <div style={{ marginTop: 8 }}>
                  {detail.clone_changes.params?.length > 0 && (
                    <div className="sm c-sl">Param changes: <span className="c-ylw">{detail.clone_changes.params.join(', ')}</span></div>
                  )}
                  {detail.clone_changes.features?.length > 0 && (
                    <div className="sm c-sl">Feature changes: <span className="c-ylw">{detail.clone_changes.features.join(', ')}</span></div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Plugins ─────────────────────────────────────────── */}
          {detail.plugin_assignments && Object.keys(detail.plugin_assignments).length > 0 && (
            <div style={{ paddingTop: 12, borderTop: '1px solid var(--bd)' }}>
              <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 10, color:'#334155', fontWeight:700 }}>Plugins</div>
              {Object.entries(detail.plugin_assignments).map(([type, snap]) => (
                <div key={type} className="flex-row sm" style={{ marginBottom: 4 }}>
                  <span className={`badge ${PLUGIN_COLORS[type] || 'b-slate'}`}>{type}</span>
                  <span>{snap.name} v{snap.version}</span>
                  {snap.validation_status === 'NOT_VALIDATED' && <span className="badge b-slate">not validated</span>}
                  {snap.validation_status === 'FAILED'        && <span className="badge b-red">validation failed</span>}
                  {snap.validation_status === 'PASSED'        && <span className="badge b-green">✓ validated</span>}
                  <span className="mono c-sl" style={{ fontSize: 9 }}>{snap.checksum?.slice(0, 8)}…</span>
                </div>
              ))}
            </div>
          )}

        </div>
        );
      })()}

      {/* Log viewer - mounts immediately on start via activeJobId, or from detail on expand */}
      {expanded && (() => {
        const jobId = activeJobId
          || (detail?.attempts?.length > 0 ? detail.attempts[detail.attempts.length - 1].job_id : null);
        const showLogs = jobId && ['RUNNING','QUEUED','WAITING_FOR_RESOURCES','COMPLETED','FAILED'].includes(run.status);
        return showLogs ? (
          <div style={{ padding:'0 16px 14px', borderTop:'1px solid var(--bd)', marginTop:0,
            background:'#F7F9FC', borderRadius:'0 0 8px 8px', border:'1px solid #C8D5E8', borderTop:'none' }}>
            <LogViewer jobId={jobId} status={run.status} />
          </div>
        ) : null;
      })()}

      {/* Package result panel - only shown when explicitly triggered */}
      {showPkg && pkgInfo && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.7)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000,
        }} onClick={() => setShowPkg(false)}>
          <div style={{
            background:'#E4EAF4', border:'1px solid var(--bd)',
            borderRadius:12, padding:28, maxWidth:520, width:'90%',
          }} onClick={e => e.stopPropagation()}>
            {pkgInfo.error ? (
              <>
                <div style={{ fontSize:16, fontWeight:700, color:'var(--red)', marginBottom:12 }}>
                  ✕ Package Failed
                </div>
                <div className="xs c-sl">{pkgInfo.error}</div>
              </>
            ) : (
              <>
                <div style={{ fontSize:16, fontWeight:700, marginBottom:16 }}>
                  📦 {pkgInfo.cached ? 'Already Packaged' : 'Model Package Ready'}
                </div>
                <div style={{ background:'#F1F5FB', borderRadius:8, padding:14, marginBottom:16 }}>
                  <div className="xs c-sl mb4">Package name</div>
                  <div className="mono" style={{ fontSize:13 }}>{pkgInfo.package_name}</div>
                </div>
                <div style={{ background:'#F1F5FB', borderRadius:8, padding:14, marginBottom:16 }}>
                  {pkgInfo.cached && (
                    <div className="xs" style={{ color:'var(--or)', marginBottom:8, cursor:'pointer' }}
                      onClick={() => { localStorage.removeItem(`aifab_pkg_${run.id}`); setPkgInfo(null); setShowPkg(false); }}>
                      ↺ Rebuild package
                    </div>
                  )}
                  <div className="xs c-sl mb4">Location on remote server ({pkgInfo.server})</div>
                  <div className="mono" style={{ fontSize:12, wordBreak:'break-all', color:'var(--or)' }}>
                    {pkgInfo.remote_path}
                  </div>
                </div>
                <div className="xs c-sl mb16">
                  Size: <strong>{pkgInfo.size}</strong> - Contains model weights, tokenizer,
                  standalone predict.py, requirements.txt, setup.sh and README.
                </div>
                <div style={{ padding:10, background:'rgba(232,71,10,.08)',
                  border:'1px solid var(--or)', borderRadius:6, fontSize:11, color:'#334155' }}>
                  To download: <span className="mono">scp aifab@{pkgInfo.server}:{pkgInfo.remote_path} .</span>
                </div>
              </>
            )}
            <button className="btn btn-ghost btn-sm" style={{ marginTop:16 }}
              onClick={() => setShowPkg(false)}>Close</button>
          </div>
        </div>
      )}

      {showDelete && (
        <DeleteRunModal
          run={run}
          onClose={() => setShowDelete(false)}
          onDeleted={() => {
            setShowDelete(false);
            if (onDeleted) onDeleted(run.id);  // optimistic removal
            onReload();                         // sync with server
          }}
        />
      )}
      {showStart && (
        <StartRunModal
          run={run}
          onClose={() => setShowStart(false)}
          onStarted={(jobId) => {
            setShowStart(false);
            if (jobId) { setActiveJobId(jobId); setExpanded(true); }
            onReload();
          }}
        />
      )}
      {showPredict && (
        <PredictModal
          versionId={detail?.model_config_ref?.version_id}
          runLabel={run.run_label || `Run #${run.run_number}`}
          runNumber={run.run_number}
          featureColumns={(() => {
            const snap = detail?.feature_config_snapshot;
            if (!snap) return [];
            const enc = snap.encoding_config || {};
            // Primary: use encoding_config role=feature - most authoritative
            const fromEnc = Object.entries(enc)
              .filter(([, v]) => typeof v === 'object' && v?.role === 'feature')
              .map(([k]) => k);
            if (fromEnc.length > 0) return fromEnc;
            // Fallback: feature_columns normalised list from backend
            const fc = snap.feature_columns;
            if (!fc) return [];
            if (Array.isArray(fc)) return fc;
            if (typeof fc === 'object') {
              return Object.entries(fc)
                .filter(([k]) => !isNaN(Number(k)))
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([, v]) => v)
                .filter(col => enc[col]?.role !== 'target' && enc[col]?.role !== 'ignore');
            }
            return [];
          })()}
          targetColumn={detail?.feature_config_snapshot?.target_column || ''}
          familyCode={run.model_family_code || detail?.model_config_ref?.family_code || ''}
          runPurpose={run.run_purpose || ''}
          onClose={() => setShowPredict(false)}
        />
      )}
      {showClone && (
        <CloneRunModal
          run={run}
          sourceDetail={detail}
          onClose={() => setShowClone(false)}
          onCloned={(clonedRun) => { setShowClone(false); onCloned(clonedRun); }}
        />
      )}
    </div>
  );
}

/* ── Start Run Modal ─────────────────────────────────────────── */
function StartRunModal({ run, onClose, onStarted }) {
  const { data: servers } = useApi('/servers');
  const [serverId,  setServerId]  = useState('');
  const [starting,  setStarting]  = useState(false);
  const [err,       setErr]       = useState(null);
  const [notify,    setNotify]    = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(() => {
    try { return JSON.parse(localStorage.getItem('aifab_user') || '{}').email || ''; }
    catch { return ''; }
  });

  const start = async () => {
    if (!serverId) { setErr('Select a server'); return; }
    if (notify && !notifyEmail) { setErr('Enter an email address for notifications'); return; }
    setStarting(true); setErr(null);
    try {
      const body = { server_id: serverId };
      if (notify && notifyEmail) body.notify_email = notifyEmail;
      const res = await apiFetch(`/runs/${run.id}/start`, { method: 'POST', body: JSON.stringify(body) });
      onStarted(res?.job_id || null);
    } catch (e) { setErr(e.message); }
    finally { setStarting(false); }
  };

  const online = (servers || []).filter(s => s.status_code === 'ONLINE' || s.status_code === 'BUSY');
  return (
    <Modal title={`Start Run #${run.run_number}`} onClose={onClose}>
      <ModalBody>
      <Err msg={err} />
      <div className="form-grp">
        <label className="form-lbl">Execution Server</label>
        <select className="form-inp" value={serverId} onChange={e => setServerId(e.target.value)}>
          <option value="">- Select Server -</option>
          {online.map(s => (
            <option key={s.id} value={s.id}>
              {s.server_name} ({s.status_code}) - {s.ram_gb}GB RAM
            </option>
          ))}
        </select>
        <div className="form-hint">
          aifab will check resource availability. If insufficient, the run queues automatically.
        </div>
      </div>

      {run.plugin_assignments && Object.keys(run.plugin_assignments).length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 10, color:'#334155', fontWeight:700 }}>Plugins in this run</div>
          {Object.entries(run.plugin_assignments).map(([type, snap]) => (
            <div key={type} className="flex-row sm" style={{ marginBottom: 6 }}>
              <span className={`badge ${PLUGIN_COLORS[type] || 'b-slate'}`}>{type}</span>
              <span>{snap.name} v{snap.version}</span>
            </div>
          ))}
        </div>
      )}

      {/* Notification preference */}
      <div className="form-grp" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: notify ? 10 : 0 }}>
          <input
            type="checkbox" id="notify-chk"
            checked={notify} onChange={e => setNotify(e.target.checked)}
            style={{ width: 15, height: 15, cursor: 'pointer' }}
          />
          <label htmlFor="notify-chk" className="form-lbl" style={{ cursor: 'pointer', marginBottom: 0 }}>
            Notify me when this run completes or fails
          </label>
        </div>
        {notify && (
          <input
            className="form-inp"
            type="email"
            value={notifyEmail}
            onChange={e => setNotifyEmail(e.target.value)}
            placeholder="your@email.com"
          />
        )}
      </div>

      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-success btn-sm" onClick={start} disabled={starting}>
          {starting ? <Spinner size={12} /> : '▶ Start Training'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ── Delete Run Modal ────────────────────────────────────────── */
function DeleteRunModal({ run, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [err,      setErr]      = useState(null);
  const isActive = ['RUNNING', 'QUEUED', 'WAITING_FOR_RESOURCES'].includes(run.status);

  const confirm = async () => {
    setDeleting(true); setErr(null);
    try {
      await apiFetch(`/runs/${run.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) { setErr(e.message); setDeleting(false); }
  };

  return (
    <Modal title={`Delete Run #${run.run_number}`} onClose={onClose}>
      <ModalBody>
      <Err msg={err} />
      {isActive ? (
        <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
          ⚠ This run is currently <strong>{run.status}</strong>. You must cancel it before deleting.
        </div>
      ) : (
        <div style={{ fontSize: 13, color: '#334155', marginBottom: 16, lineHeight: 1.6 }}>
          Delete <strong style={{ color: '#0F172A' }}>{run.run_label || `Run #${run.run_number}`}</strong>?
          <br />
          <span style={{ color: '#64748B', fontSize: 12 }}>
            Training history, logs, and metrics for this run will be permanently removed.
            The trained model artifact on the remote server will not be deleted.
          </span>
        </div>
      )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        {!isActive && (
          <button className="btn btn-sm" style={{ background: 'var(--red)', color: '#FFFFFF' }}
            onClick={confirm} disabled={deleting}>
            {deleting ? <Spinner size={12} /> : 'Delete Run'}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}